import React from 'react';
import { motion } from 'framer-motion';
const testimonials = [
  {
    quote:
      'CustomSync has helped us save countless hours every week by automating our Amazon/Etsy custom workflow. The founder Nick is quick to respond, detail-oriented, and just really great to work with.',
    author: 'Ari',
    company: 'Monjo',
    companyUrl: 'https://www.linkedin.com/company/monjo-co/',
    highlight: 'save countless hours',
    logo: '/images/testimonials/monjo_co_logo.jpeg',
  },
  {
    quote:
      'CustomSync has completely transformed the way we process Amazon customised orders. Effectively allowing us to increase our output whilst reducing our error rate!',
    author: 'Paolo',
    company: 'TheDogTagCo',
    companyUrl: 'https://thedogtag.co/',
    highlight: 'increase our output',
    logo: '/images/testimonials/dogtagco_logo.jpg',
  },
  {
    quote:
      'CustomSync has shaved hours off my daily workload, downloading tasks that used to slow everything down. It\'s become an essential tool for my business, saving time and keeping operations running smoothly.',
    author: 'Adam',
    company: 'CustomBrandWare',
    companyUrl: 'https://www.amazon.com/stores/CustomBrandWare/page/75418AEB-A058-4EF1-84B7-94A855781D92',
    highlight: 'shaved hours off my daily workload',
    logo: '/images/testimonials/custombrandware_logo.jpeg',
  },
];

const StarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="#FACC14"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5 text-yellow-400"
    aria-hidden="true"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
  </svg>
);

const Testimonials = () => {
  return (
    <section className="bg-white py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Divider with "Trusted by" and company logos */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="relative flex items-center justify-center my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative bg-white px-4">
              <span className="text-md font-medium">Trusted by</span>
            </div>
          </div>
          <div className="flex justify-center items-center gap-28 flex-wrap">
            {testimonials.map((testimonial) => (
              testimonial.companyUrl ? (
                <a
                  key={testimonial.company}
                  href={testimonial.companyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                >
                  <img
                    src={testimonial.logo}
                    alt={`${testimonial.company} logo`}
                    className="h-28 shadow-md"
                  />
                </a>
              ) : (
                <img
                  key={testimonial.company}
                  src={testimonial.logo}
                  alt={`${testimonial.company} logo`}
                  className="h-28 shadow-md"
                />
              )
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Used by Sellers Who Personalize at Scale</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Hear from Amazon custom sellers who rely on CustomSync to automate their personalization workflow.
          </p>
        </motion.div>

        <div className="grid gap-8 md:grid-cols-2">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.author}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="pb-8 w-full sm:px-4"
            >
              <figure className="rounded-2xl border border-gray-200 bg-stone-50 p-6 text-base leading-7 shadow transition-shadow duration-300 hover:shadow-lg">
                <blockquote className="text-stone-900">
                  <p>
                    “
                    {testimonial.highlight && testimonial.quote.includes(testimonial.highlight) ? (
                      (() => {
                        const [start, end] = testimonial.quote.split(testimonial.highlight);
                        return (
                          <span>
                            {start}
                            <span className="bg-amber-100 font-medium text-stone-900">{testimonial.highlight}</span>
                            {end}
                          </span>
                        );
                      })()
                    ) : (
                      <span>{testimonial.quote}</span>
                    )}
                    ”
                  </p>
                </blockquote>
                <figcaption className="mt-6 flex items-start gap-4">
                  {testimonial.logo && (
                    testimonial.companyUrl ? (
                      <a
                        href={testimonial.companyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0"
                      >
                        <img
                          src={testimonial.logo}
                          alt={`${testimonial.company} logo`}
                          className="h-20 w-20 rounded-full object-cover shadow-md hover:opacity-80 transition-opacity cursor-pointer"
                        />
                      </a>
                    ) : (
                      <img
                        src={testimonial.logo}
                        alt={`${testimonial.company} logo`}
                        className="h-20 w-20 rounded-full object-cover flex-shrink-0 shadow-md"
                      />
                    )
                  )}
                  <div className="flex flex-col flex-1">
                    <span className="font-semibold text-stone-900">{testimonial.author}</span>
                    {testimonial.company && (
                      testimonial.companyUrl ? (
                        <a
                          className="text-xs font-semibold text-stone-500 hover:underline"
                          href={testimonial.companyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {testimonial.company}
                        </a>
                      ) : (
                        <span className="text-xs font-semibold text-stone-500">{testimonial.company}</span>
                      )
                    )}
                    <div className="mt-3 flex gap-1">
                      {[...Array(5)].map((_, starIndex) => (
                        <StarIcon key={starIndex} />
                      ))}
                    </div>
                  </div>
                </figcaption>
              </figure>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;

